/**
 * TasksSummaryCard — Testing Notes 12 · #1
 * -----------------------------------------
 * Compact home card that shows the 3 task KPIs (open · mine · overdue) plus a
 * jump-to-list link. Fetches `/api/tasks/summary` on mount. Kept tiny so it
 * lives comfortably alongside other Home cards.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ClipboardList, ArrowRight, AlertCircle, CheckCircle2 } from "lucide-react";

import { useTranslation } from "react-i18next";
export default function TasksSummaryCard() {
  const { t: __t } = useTranslation();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let dead = false;
    const load = () => {
      api.get("/tasks/summary")
        .then(({ data }) => !dead && setSummary(data))
        .catch(() => !dead && setSummary({ open: 0, my_open: 0, overdue: 0 }));
    };
    load();
    const t = setInterval(load, 60000);
    return () => { dead = true; clearInterval(t); };
  }, []);

  if (!summary) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid="tasks-summary-card-loading">
        <div className="h-6 w-40 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  const nothingToDo = summary.open === 0 && summary.overdue === 0;

  return (
    <Link
      to="/tasks"
      className="group block rounded-2xl border border-border bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40"
      data-testid="tasks-summary-card"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ClipboardList className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">To-do &amp; reminders</div>
            <div className="text-[10px] text-muted-foreground">Assign · track · never forget</div>
          </div>
        </div>
        <ArrowRight className="h-4 w-4 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:text-primary group-hover:opacity-100" />
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <KpiTile testId="tasks-kpi-open" label={__t("components.taskssummarycard.open")} value={summary.open} tone="primary" />
        <KpiTile testId="tasks-kpi-mine" label={__t("components.taskssummarycard.assigned_to_me")} value={summary.my_open} tone="info" />
        <KpiTile testId="tasks-kpi-overdue" label={__t("components.taskssummarycard.overdue")} value={summary.overdue} tone={summary.overdue > 0 ? "bad" : "neutral"} icon={summary.overdue > 0 ? AlertCircle : null} />
      </div>

      {nothingToDo && (
        <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5" /> All caught up — no open tasks.
        </div>
      )}
    </Link>
  );
}

function KpiTile({ testId, label, value, tone = "neutral", icon: Icon }) {
  const tones = {
    neutral: "bg-secondary text-foreground",
    primary: "bg-primary/10 text-primary",
    info: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
    bad: "bg-red-500/10 text-red-700 dark:text-red-400",
  };
  return (
    <div className={"rounded-lg p-2 " + tones[tone]} data-testid={testId}>
      <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest opacity-80">
        {Icon && <Icon className="h-2.5 w-2.5" />}
        {label}
      </div>
      <div className="mt-0.5 font-mono text-xl font-black tabular-nums">{value}</div>
    </div>
  );
}
